import { config } from '@iheartjane/config';
import { CardCarousel } from '@iheartjane/menu';
import { Flex } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import isEqual from 'lodash-es/isEqual';
import { useEffect, useMemo, useState } from 'react';

import { useCartTopperRow } from '@jane/dm/internal';
import { FilterSortId } from '@jane/search/types';
import {
  ErrorBoundary,
  MenuProductCarousel,
} from '@jane/shared-ecomm/components';
/* eslint-disable-next-line no-restricted-imports */
import type { Store, StoreSpecial } from '@jane/shared/models';
import type { PendingCartProduct } from '@jane/shared/types';

import { HitProductCard } from './HitProductCard';

const MENU_PRODUCTS_BY_PRICE_ASC = () =>
  `menu-products-by-price-${config.algoliaEnv}`;

interface CartToppersProps {
  cartProducts: PendingCartProduct[];
  onAddToCart: (productId: number, priceId: PriceId, count: number) => void;
  specials?: StoreSpecial[];
  store: Store;
}
export const CartToppers = ({
  cartProducts,
  onAddToCart,
  specials,
  store,
}: CartToppersProps) => {
  const [cartProductIds, setCartProductIds] = useState<number[]>([]);

  const rawProductIds = useMemo(
    () => cartProducts.map((product) => product.id),
    [cartProducts]
  );

  useEffect(() => {
    if (!isEqual(cartProductIds, rawProductIds)) {
      setCartProductIds(rawProductIds);
    }
  }, [cartProductIds, rawProductIds]);

  const { instance, isLoading } = useCartTopperRow({
    cartProductIds,
    searchAttributes: ['*'],
    storeId: Number(store.id),
  });

  useEffect(() => {
    instance?.onLoad();
  }, [instance]);

  if (!store || (!isLoading && !!instance && !instance?.products.length))
    return null;

  return (
    <ErrorBoundary withFallbackRender={false}>
      <Flex flexDirection="column" mt={16} px={24} background="background">
        <MenuProductCarousel
          isLoading={isLoading}
          name="Add to your order"
          listView={false}
          variant="default"
        >
          {instance?.products.map((product, index) => (
            <CardCarousel.Card
              key={`row-ad-${product.objectId}`}
              width="100%"
              widthMobile="100%"
            >
              <HitProductCard
                algoliaIndexName={MENU_PRODUCTS_BY_PRICE_ASC()}
                appliedWeightFilter={''}
                bucketName="cart-toppers"
                cartProducts={cartProducts}
                cartStoreId={store.id}
                columnPosition={index}
                hit={product.attributes}
                onAddToCart={onAddToCart}
                productInstance={product}
                productLocation="Cart Toppers"
                searchState={{
                  currentSort: {
                    id: FilterSortId.PriceAsc,
                    suffix: 'by-price-asc',
                  },
                }}
                specials={specials}
                store={store}
              />
            </CardCarousel.Card>
          ))}
        </MenuProductCarousel>
      </Flex>
    </ErrorBoundary>
  );
};
